<template>
  <div>
    <h2 class="font-bold text-18px pb-0.5">Screenshots</h2>
    <div v-loading="loading">
      <template v-if="screenshots.length">
        <div
          class="bg-white rounded-md border border-grayNeutralLight p-2 mb-0.5 last:mb-0"
          v-for="(asset, index) in screenshots"
          :key="index"
        >
          <div class="flex items-center gap-1 w-full mb-2">
            <div class="flex items-center gap-1 cursor-pointer" @click="toggle(index)">
              <template v-if="asset.screenshots.length">
                <i :class="isOpen(index) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                <svg v-if="icon" width="14" height="16">
                  <use :xlink:href="`#${icon}`"></use>
                </svg>
                <svg width="16" height="17">
                  <use :xlink:href="`#screen`"></use>
                </svg>
              </template>
              <h3 class="font-bold text-dark mb-0">{{ asset.name }}</h3>
              <span
                v-if="asset.screenshots.length"
                class="w-3.5 h-3.5 text-[#909399] bg-grayLightest inline-flex justify-center items-center rounded-full font-bold"
              >
                {{ asset.screenshots.length }}
              </span>
            </div>
            <ElButton
              v-if="screenshots.length && canManageScreenshots"
              type="secondary"
              @click="uploadScreenshot(asset)"
              class="ml-auto bg-primary/10 text-primary button-custom-icon"
            >
              <svg width="16" height="16">
                <use :xlink:href="`#upload-arrow`"></use>
              </svg>
              Upload
            </ElButton>
          </div>

          <div v-if="asset.screenshots.length" v-show="isOpen(index)">
            <div class="flex items-center gap-2 py-0.5" v-for="(item, index) in asset.screenshots" :key="index">
              <img v-if="item.thumbnail_url" :src="imageSignature(item.thumbnail_url)" alt="screenshot" class="w-5 h-5 rounded shrink-0" />
              <div v-else class="p-2 bg-[#F9F9FB] border border-grayNeutralLight rounded w-5 h-5 shrink-0">
                <svg width="16" height="17">
                  <use :xlink:href="`#screen`"></use>
                </svg>
              </div>
              <span>{{ item.name }}</span>
              <span class="ml-auto text-gray mr-2">{{ item.size | formatSize }}</span>
              <!-- Delete -->
              <ElButton v-if="canManageScreenshots" type="text" class="text-grayNeutralDark p-1" @click="deleteScreenshot(item)">
                <svg width="16" height="16">
                  <use :xlink:href="`#delete-slim`"></use>
                </svg>
              </ElButton>
              <!-- View Screenshot -->
              <ElButton @click="viewScreenshot(asset, index)" type="text" class="text-grayNeutralDark p-1 ml-0">
                <svg width="16" height="16">
                  <use :xlink:href="`#preview`"></use>
                </svg>
              </ElButton>
            </div>
          </div>

          <!-- No screenshots found -->
          <div v-else>
            <NoDataCard>
              <template slot="title">No screenshots</template>
              <template v-if="canManageScreenshots" slot="info">
                <p>Upload screen captures of implemented creatives here.</p>
                <UploadFiles
                  class="mt-1"
                  :validate="false"
                  @done="uploadDone"
                  :options="{
                    url: '/api/social-products/social-boost/screenshots/' + asset.id,
                    accept: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/avi', 'video/mov'],
                  }"
                />
              </template>
              <p v-else slot="info">Screenshots will be displayed here once available.</p>
            </NoDataCard>
          </div>
        </div>
      </template>
      <!-- No assets found -->
      <NoDataCard v-else>
        <template slot="title">No screenshots found</template>
        <template slot="info">Screenshots will be displayed here once available.</template>
      </NoDataCard>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import NoDataCard from '@/components/social-products/social-boost/parts/NoDataCard.vue'
import LayoutModalSlider from '@/components/social-products/social-boost/modal/creative/LayoutSlider.vue'
import UploadFiles from '@/components/social-products/social-boost/parts/UploadFiles.vue'
import DeleteScreenshot from '@/components/social-products/social-boost/modal/screenshot/DeleteScreenshot.vue'
import UploadScreenshot from '@/components/social-products/social-boost/modal/screenshot/UploadScreenshot.vue'
import ScreenshotModal from '@/components/social-products/social-boost/modal/screenshot/ScreenshotModal.vue'

export default {
  components: {
    NoDataCard,
    UploadFiles,
    DeleteScreenshot,
    UploadScreenshot,
    ScreenshotModal,
  },
  props: {
    icon: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      openStates: {},
      loading: false,
    }
  },
  filters: {
    formatSize(size) {
      if (!size || isNaN(size)) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(size) / Math.log(1024))
      const result = size / Math.pow(1024, i)
      return result.toFixed(1) + ' ' + units[i]
    },
  },
  created() {
    this.init()
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaign']),
    flight() {
      return this.campaign?.flights?.find((f) => f.id === Number(this.$route.params.flight_id))
    },
    flightStatus() {
      return this.flight?.status
    },
    screenshots() {
      return this.$store.getters['socialBoostCampaign/screenshots'] || []
    },
    canManageScreenshots() {
      return this.$canAccess(['traffic-manager']) && this.$SBCampaignStatusAccess(['active'], this.flightStatus)
    },
  },
  methods: {
    uploadDone() {
      this.init()
    },
    async init() {
      this.loading = true
      await this.$store.dispatch('socialBoostCampaign/getScreenshots', { flight_id: this.$route.params.flight_id })
      if (this.screenshots.length > 0) {
        this.$set(this.openStates, 0, true)
      }
      this.loading = false
    },
    toggle(index) {
      this.$set(this.openStates, index, !this.openStates[index])
    },
    isOpen(index) {
      return this.openStates[index] || false
    },
    imageSignature(img) {
      return this.signature(img, 'socialproducts', 1)
    },
    deleteScreenshot(screenshot) {
      this.$Modal.init(
        {
          propsData: {
            component: DeleteScreenshot,
            meta: { screenshot },
            size: '480px',
          },
        },
        this
      )
    },
    uploadScreenshot(asset) {
      this.$Modal.init(
        {
          propsData: {
            component: UploadScreenshot,
            meta: {
              asset,
              onUploaded: () => this.init(),
            },
            size: '480px',
          },
        },
        this
      )
    },
    viewScreenshot(asset, selectedIndex) {
      this.$Modal.init(
        {
          propsData: {
            component: ScreenshotModal,
            meta: {
              screenshots: (asset.screenshots || []).map((s) => ({
                url: this.imageSignature(s.url || s.url),
                name: s.name,
                size: s.size,
                dimensions: s.dimensions || { width: '-', height: '-' },
                file_type: s.file_type,
                thumbnail_url: this.imageSignature(s.thumbnail_url || s.thumbnail_url),
              })),
              currentIndex: selectedIndex,
            },
            size: '1200px',
          },
          layout: LayoutModalSlider,
          callback(payload) {
            if (payload.event === 'modal:close') {
              // optional: handle modal close
            }
          },
        },
        this
      )
    },
  },
}
</script>
<style lang="scss">
.button-custom-icon {
  span {
    display: flex;
  }
}
</style>
